//
import { isObject, deepEquals, isEmpty } from 'app/utils/utils';
import { get,set } from 'app/utils/lo/lo';
import history from 'store/History';
import version from 'app/config/version';
import { findAttribute, normalizeEntityType } from '../classification/classificationUtils';

console.log('platform version', version); // eslint-disable-line no-console

const _parseVersionLabel = versions => {
    if (!versions) {
        return 'Version unavailable';
    }
    const versionUI = (version.build.version || version.version).split('.').map(Number);
    const bpmn = (versions['bpmn']?.version || '').split('.').map(Number);
    const graphql = (versions['graphql']?.version || '').split('.').map(Number);
    const database = (versions['database']?.version || '').split('.').map(Number);
    return `Version ${version?.version?.[0]}.${bpmn[1] + graphql[1] + database[1] + versionUI[1]}.${bpmn[2] + graphql[2] + database[2] + versionUI[2]}`;
};

export const parseVersionLabel = (menu, versions) => {
    return menu.map(menu => {
        if (menu.icon === 'information-variant') {
            return {
                ...menu,
                label: _parseVersionLabel(versions),
            };
        }
        return menu;
    });
};

// TODO: move these two functions to date utils
// TODO: change isDate name to isDateObject
export const isDate = value => {
    return value && typeof value.getMonth === 'function';
};

export const convertTISOString = value => {
    if (isDate(value)) {
        return value.toISOString();
    }
    return value;
};

const FILE_FIELD_TYPES = new Set(['dropzone', 'image', 'file']);

/**
 * Builds a Set of file field names from a form component tree.
 * Traverses nested children (panels, groups, groupRepeat) recursively.
 * Detection is by component type, not value shape.
 */
export const buildFileFieldsSet = (components) => {
    const fileFields = new Set();
    const traverse = (fields) => {
        (fields || []).forEach(field => {
            const fieldName = get(field, 'properties.name');
            if (FILE_FIELD_TYPES.has(field.type) && fieldName) {
                fileFields.add(fieldName);
            }
            if (field.children) traverse(field.children);
        });
    };
    traverse(components);
    return fileFields;
};

const _mergeObjectKeys = (original, merged, subscription, fileFields) => {
    const result = { ...merged };
    const allKeys = new Set([...Object.keys(subscription), ...Object.keys(result)]);
    for (const key of allKeys) {
        if (key in subscription) {
            // File fields at any nesting depth — subscription always wins
            if (fileFields?.has(key)) {
                result[key] = subscription[key];
            } else {
                const origChild = original && typeof original === 'object' ? original[key] : undefined;
                result[key] = _mergeField(origChild, result[key], subscription[key], fileFields);
            }
        } else if (original && typeof original === 'object' && key in original) {
            // Key was in original but removed in subscription → intentional deletion
            delete result[key];
        }
        // Key only in form (not in original or subscription) → UI-generated, keep it
    }
    return result;
};

/**
 * Recursively merges a single field value from subscription, preserving user edits.
 * - Primitives: compare original vs form to detect user edit. If user edited → keep form. If not → use subscription.
 * - Objects: recurse into each key via _mergeObjectKeys. File field keys always take subscription value.
 * - Keys only in subscription → include them (new server data).
 * - Keys only in form → keep them (UI-generated).
 */
// Date-only pickers store values as "YYYY-MM-DD" strings while the server returns Date objects.
// This converts a date string to a Date for comparison purposes only — form value is never mutated.
const _normalizeDateString = value => {
    if (!value || typeof value !== 'string') return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d;
};

// DateTimeRange is an atomic field — partial key merging produces invalid states.
// Detects both relative {relative, range, amount, unit} and absolute {startDate, endDate} shapes.
const _isDateTimeRangeObject = obj =>
    obj && typeof obj === 'object' && !Array.isArray(obj) &&
    (('relative' in obj && 'range' in obj && 'amount' in obj && 'unit' in obj) ||
        ('startDate' in obj || 'endDate' in obj));

const _mergeField = (original, form, subscription, fileFields) => {
    // If subscription value is an object (not array, not null, not Date) → recurse key by key
    if (subscription && typeof subscription === 'object' && !Array.isArray(subscription) && !isDate(subscription)) {
        // DateTimeRange: treat as atomic — if user changed any value inside, preserve their entire value
        if (_isDateTimeRangeObject(subscription) && form && typeof form === 'object') {
            const userChangedAnyValue = Object.keys(form).some(key => !_isEqual(get(original, key), form[key]));
            if (userChangedAnyValue) return form;
        }
        return _mergeObjectKeys(original, form && typeof form === 'object' ? form : {}, subscription, fileFields);
    }

    // Leaf value (primitive, array, or null) — check if user modified it.
    const isOrigEmpty = original === null || original === undefined || original === '';
    const isFormEmpty = form === null || form === undefined || form === '';
    const formForComparison = isDate(original) ? _normalizeDateString(form) : form;
    const userModified = (isOrigEmpty && isFormEmpty) ? false : !_isEqual(original, formForComparison);
    return userModified ? form : subscription;
};

const _isEqual = (v1, v2) => {
    if (isDate(v1) || isDate(v2)) {
        // From GQL we receive the ISO format of date but when change the date
        const d1 = convertTISOString(v1); // from date field it becomes a date object
        const d2 = convertTISOString(v2);
        return d1 === d2;
    }
    if ((Array.isArray(v1) && !Array.isArray(v2)) || (Array.isArray(v2) && !Array.isArray(v1))) {
        return false;
    }
    if (typeof v1 !== typeof v2) {
        return false;
    }
    if (isEmpty(v1) === !isEmpty(v2) || !isEmpty(v1) === isEmpty(v2)) {
        return false;
    }
    if (typeof v1 === 'string' || typeof v1 === 'number' || typeof v1 === 'boolean') {
        return v1 === v2;
    }
    if (isObject(v1) && isObject(v2)) {
        return deepEquals(v1, v2);
    }
    if (Array.isArray(v1) && Array.isArray(v2)) {
        if (v1.length !== v2.length) {
            return false;
        }
        return !v1.map((val, i) => _isEqual(val, v2[i])).includes(false);
    }

    return true;
};

export const getOnlyUpdatedData = (data, formData) => {
    let record = {};
    const primary = {};
    Object.entries(formData).forEach(([key, value]) => {
        if (key === 'id') {
            record[key] = value; // Always keeping the ID
        } else if (key === 'primary' && !isEmpty(value)) {
            Object.entries(value).forEach(([pKey, pValue]) => {
                if (!_isEqual(pValue, get(data, `primary.${pKey}`))) {
                    primary[pKey] = pValue;
                }
            });
        } else if (!_isEqual(value, data[key])) {
            record[key] = value;
        }
    });
    if (!isEmpty(primary)) {
        record = { ...record, primary };
    }
    return record;
};

export const getOnlyUpdatedObjectData = (data, formData) => {
    const record = {};
    Object.entries(formData).forEach(([key, value]) => {
        if (!_isEqual(value, data[key])) {
            record[key] = value;
        }
    });
    return record;
};

export const filterAttributesAsPerDefinitions = (definitions, attributes) => {
    if (Array.isArray(attributes)) {
        return attributes.map(att => _filterAttributesAsPerDefinitions(definitions, att));
    } else {
        return _filterAttributesAsPerDefinitions(definitions, attributes);
    }
};

const _filterAttributesAsPerDefinitions = (definitions, attributes) => {
    if (!isObject(attributes)) {
        return null;
    }
    return Object.keys(attributes).reduce((data, attName) => {
        const attValue = attributes[attName];
        const attribute = findAttribute(definitions, attName);
        if (!attribute) return data;
        if (attribute?.type === 'group' && isObject(attValue)) {
            const nestedAttributes = filterAttributesAsPerDefinitions(definitions, attValue);
            data[attName] = nestedAttributes;
        } else if (attribute?.type === 'groupRepeat' && Array.isArray(attValue)) {
            const nestedArray = attValue.map(item => filterAttributesAsPerDefinitions(definitions, item));
            data[attName] = nestedArray;
        } else if (Array.isArray(attValue)) {
            data[attName] = attValue.filter(Boolean);
        } else {
            // checking isEmpty only when the type of attribute value is object so it do not interfere with numbers, strings or booleans
            data[attName] = typeof attValue === 'object' && isEmpty(attValue) ? null : attValue;
        }
        return data;
    }, {});
};

export const openSidebarByParams = props => {
    const { id, type, title, internal, useDefaultTab, openClassSidebar, reloadList,
        openTaskSidebar, openEntitySidebar, openProcessSidebar, openWorkspaceSidebar } = props;
    const normalizedType = normalizeEntityType(type);
    const params = { id, type, title, reloadList, internal: !!internal, useDefaultTab };
    switch (normalizedType) {
        case 'class':
            openClassSidebar(params);
            break;
        case 'task':
        case 'system_task':
            if (title === 'Form') {
                history.push(`/abox/task/${id}/form`);
                return;
            }
            if (title === 'Relations') {
                history.push(`/abox/task/${id}/relationships`);
                return;
            }
            if (title === 'Process Map') {
                history.push(`/abox/task/${id}/process-map`);
                return;
            }
            openTaskSidebar(params);
            break;
        case 'process':
        case 'system_process':
            if (title === 'Relations') {
                history.push(`/abox/process/${id}/relationships`);
                return;
            }
            openProcessSidebar(params);
            break;
        case 'workspace':
            openWorkspaceSidebar(params);
            break;
        case 'user':
            if (title === 'Relations') {
                history.push(`/user-management/${type}/${id}/relationships`);
                return;
            }
            openEntitySidebar({ ...params, userAbout: true });
            break;
        case 'eventtype':
            this.props.openEventTypeSidebar(params);
            break;
        case 'event':
            this.props.openEventSidebar({ ...params, time: params.time });
            break;
        case 'team':
            this.props.openTeamSidebar(params);
            break;
        default:
            if (title === 'Relations') {
                history.push(`/entities/${type}/${id}/relationships`);
                return;
            }
            openEntitySidebar(params);
            break;
    }
    return;
};

/**
 * Applies subscription changes to data for tracked fields.
 *
 * @param {Object} data - The data to update
 * @param {Object} subscriptionData - New data received from subscription
 * @param {Array} trackedFields - Array of field paths to track: ['name', 'primary.assignee', ...]
 * @returns {Object} - Data with subscription changes applied
 */
export const applySubscriptionChanges = (data, subscriptionData, trackedFields = []) => {
    if (!data || !subscriptionData) return data;

    let result = { ...data };
    for (const field of trackedFields) {
        const value = get(subscriptionData, field);
        if (value !== undefined) {
            result = set(result, field, value);
        }
    }
    return result;
};

/**
 * Merges subscription data with current form data, preserving user's local changes.
 * Pass `components` (the form definition array) so file/image fields always take the subscription value.
 *
 * @param {Object} originalData - The original data (last saved/loaded state from server)
 * @param {Object} currentFormData - Current form data (may include user modifications)
 * @param {Object} subscriptionData - New data received from subscription
 * @param {Array} trackedFields - Array of field paths to track: ['name', 'primary.assignee', ...]
 * @param {Array} components - Form component definitions used to detect file fields (optional)
 * @returns {Object} - Merged data with user changes preserved
 */
export const mergeEntityChanges = (originalData, currentFormData, subscriptionData, trackedFields = [], components = null) => {
    if (!originalData || !subscriptionData) return currentFormData;

    const fileFields = components ? buildFileFieldsSet(components) : null;
    let mergedData = { ...(currentFormData || {}) };

    const fields = trackedFields.length ? trackedFields : Object.keys(subscriptionData);

    for (const field of fields) {
        const subscriptionValue = get(subscriptionData, field);
        if (subscriptionValue === undefined) continue;

        // File fields are arrays at the top level — skip _mergeField entirely, subscription always wins
        if (fileFields?.has(field)) {
            mergedData = set(mergedData, field, subscriptionValue);
            continue;
        }

        const originalValue = get(originalData, field);
        const formValue = get(mergedData, field);
        mergedData = set(mergedData, field, _mergeField(originalValue, formValue, subscriptionValue, fileFields));
    }

    return mergedData;
};

/**
 * Handles subscription changes for form-based components.
 * Extracts common logic for merging subscription data and updating Redux.
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.formRef - React ref to the form component (used to get current form data via isValidForm)
 * @param {Object} params.formData - Current form data passed directly (alternative to formRef)
 * @param {Object} params.reduxState - Current Redux state (details)
 * @param {Object} params.subscriptionData - Data received from subscription
 * @param {Array} params.trackedFields - Fields to track for updates
 * @param {Function} params.updateRedux - Action to update Redux state
 * @returns {Promise<Object|null>} - Merged data or null if form not ready
 */
export const processSubscriptionUpdate = async ({ formRef, formData, reduxState, subscriptionData, trackedFields, updateRedux }) => {
    let currentData = formData;
    if (!currentData) {
        if (!formRef?.current) return null;
        const form = await formRef.current.isValidForm();
        currentData = form.data;
    }

    // Merge subscription data with current form, preserving user's local changes
    const mergedData = mergeEntityChanges(reduxState, currentData, subscriptionData, trackedFields);

    // Apply subscription updates to Redux state
    const newReduxState = applySubscriptionChanges(reduxState, subscriptionData, trackedFields);

    // Update Redux
    updateRedux(newReduxState);

    return mergedData;
};
